import { useCallback, useEffect, useRef, useState } from "react";

export type SelfViewError = "mic-blocked" | "unsupported";

const VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  facingMode: "user",
  width: { ideal: 960 },
  height: { ideal: 720 },
};

export const isDemoSupported = () =>
  typeof window !== "undefined" &&
  Boolean(navigator.mediaDevices?.getUserMedia) &&
  "RTCPeerConnection" in window;

/**
 * Local camera + mic preview for the demo. The camera never leaves the browser: only LiveKit's
 * own microphone track is published. The mic meter writes a CSS variable on `meterRef` so the
 * level animates without re-rendering React.
 */
export const useSelfView = () => {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState<SelfViewError | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const meterRef = useRef<HTMLElement | null>(null);
  const meterCleanupRef = useRef<(() => void) | null>(null);

  const stopMeter = useCallback(() => {
    meterCleanupRef.current?.();
    meterCleanupRef.current = null;
    meterRef.current?.style.setProperty("--mic-level", "0");
  }, []);

  const startMeter = useCallback(
    (audioTrack: MediaStreamTrack) => {
      stopMeter();
      const AudioContextClass =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioContextClass) return;

      const context = new AudioContextClass();
      const analyser = context.createAnalyser();
      analyser.fftSize = 512;
      context.createMediaStreamSource(new MediaStream([audioTrack])).connect(analyser);
      const samples = new Uint8Array(analyser.fftSize);
      let frame = 0;

      const tick = () => {
        analyser.getByteTimeDomainData(samples);
        let sum = 0;
        for (const sample of samples) sum += ((sample - 128) / 128) ** 2;
        const level = audioTrack.enabled ? Math.min(Math.sqrt(sum / samples.length) * 4, 1) : 0;
        meterRef.current?.style.setProperty("--mic-level", level.toFixed(3));
        frame = requestAnimationFrame(tick);
      };
      tick();

      meterCleanupRef.current = () => {
        cancelAnimationFrame(frame);
        void context.close();
      };
    },
    [stopMeter]
  );

  const publish = (next: MediaStream) => {
    streamRef.current = next;
    setStream(new MediaStream(next.getTracks()));
  };

  const start = useCallback(async (): Promise<SelfViewError | null> => {
    if (!isDemoSupported()) {
      setError("unsupported");
      return "unsupported" as const;
    }

    setRequesting(true);
    setError(null);
    try {
      let media: MediaStream;
      try {
        media = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: VIDEO_CONSTRAINTS,
        });
      } catch {
        // Camera blocked or missing is fine; the interview only needs the mic.
        media = await navigator.mediaDevices.getUserMedia({ audio: true });
      }

      const audioTrack = media.getAudioTracks()[0];
      if (audioTrack) startMeter(audioTrack);
      setCameraOn(media.getVideoTracks().length > 0);
      setMicOn(true);
      publish(media);
      return null;
    } catch {
      setError("mic-blocked");
      return "mic-blocked" as const;
    } finally {
      setRequesting(false);
    }
  }, [startMeter]);

  const toggleCamera = useCallback(async () => {
    const current = streamRef.current;
    if (!current) return;

    const videoTracks = current.getVideoTracks();
    if (videoTracks.length > 0) {
      // Stop rather than disable, so the camera light goes off too.
      for (const track of videoTracks) {
        track.stop();
        current.removeTrack(track);
      }
      setCameraOn(false);
      publish(current);
      return;
    }

    try {
      const video = await navigator.mediaDevices.getUserMedia({ video: VIDEO_CONSTRAINTS });
      for (const track of video.getVideoTracks()) current.addTrack(track);
      setCameraOn(true);
      publish(current);
    } catch {
      setCameraOn(false);
    }
  }, []);

  const toggleMic = useCallback(() => {
    setMicOn((on) => {
      for (const track of streamRef.current?.getAudioTracks() ?? []) track.enabled = !on;
      return !on;
    });
  }, []);

  // LiveKit captures its own mic track; release the preview one before connecting.
  const releaseAudio = useCallback(() => {
    stopMeter();
    const current = streamRef.current;
    if (!current) return;
    for (const track of current.getAudioTracks()) {
      track.stop();
      current.removeTrack(track);
    }
    publish(current);
  }, [stopMeter]);

  const stop = useCallback(() => {
    stopMeter();
    for (const track of streamRef.current?.getTracks() ?? []) track.stop();
    streamRef.current = null;
    setStream(null);
    setCameraOn(false);
  }, [stopMeter]);

  useEffect(() => stop, [stop]);

  return {
    stream,
    cameraOn,
    micOn,
    setMicOn,
    requesting,
    error,
    meterRef,
    start,
    stop,
    toggleCamera,
    toggleMic,
    releaseAudio,
  };
};
